import { getCertStatus, getTrainStatus, type ComplianceStatus } from "@/lib/compliance-status";
import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCardsSkeleton, CardGridSkeleton, TableSkeleton } from "@/components/PageSkeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import {
  ShieldCheck, Search, CheckCircle2, AlertTriangle, XCircle,
  Loader2, ChevronRight, Users, BookOpen, Plus, LayoutGrid, Clock,
} from "lucide-react";
import {
  apiGetCertificationOverview, apiGetTrainingOverview, apiGetDepartments,
  apiGetCertificateTypes, apiGetTrainingTypes, apiGetEmployees,
  apiCreateQualification,
  type CertComplianceEmployee, type TrainingComplianceEmployee,
  type CertificateType, type TrainingType,
} from "@/lib/api";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";

const COMPLIANCE_COLORS: Record<ComplianceStatus, string> = {
  compliant: "#10b981",
  expiring: "#f59e0b",
  non_compliant: "#ef4444",
  noData: "#64748b",
  notApplicable: "#94a3b8",
};
const DEPT_CHART_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#14b8a6"];

// ─── Types ───────────────────────────────────────────────────────────────────

const STATUS_CFG: Record<ComplianceStatus, { label: string; icon: any; badge: string }> = {
  compliant:     { label: "Compliant",     icon: CheckCircle2,  badge: "bg-emerald-500/10 text-emerald-700 border-emerald-200" },
  expiring:      { label: "Expiring",      icon: AlertTriangle, badge: "bg-amber-500/10 text-amber-700 border-amber-200"       },
  non_compliant: { label: "Action needed", icon: XCircle,       badge: "bg-red-500/10 text-red-700 border-red-200"             },
  noData: { label: "No data", icon: AlertTriangle, badge: "bg-muted text-muted-foreground border-border" },
  notApplicable: { label: "Not applicable", icon: ShieldCheck, badge: "bg-muted text-muted-foreground border-border" },
};

// ─── Shared label/input helpers ───────────────────────────────────────────────

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="text-xs font-medium text-muted-foreground block mb-1">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

function EmployeePicker({
  allEmployees,
  empId,
  onSelect,
  placeholder,
}: {
  allEmployees: { id: string | number; name: string }[];
  empId: string;
  onSelect: (id: string, name: string) => void;
  placeholder: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen]   = useState(false);

  const selected = empId ? allEmployees.find(e => String(e.id) === empId) : null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allEmployees
      .filter(e => (e.name ?? "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [allEmployees, query]);

  function handleSelect(e: { id: string | number; name: string }) {
    onSelect(String(e.id), e.name);
    setQuery("");
    setOpen(false);
  }

  function handleInputChange(v: string) {
    setQuery(v);
    if (empId) onSelect("", "");
    setOpen(true);
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={selected ? selected.name : query}
        onChange={e => handleInputChange(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
        autoComplete="off"
      />
      {open && !selected && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-md max-h-48 overflow-auto text-sm">
          {filtered.map(e => (
            <li
              key={e.id}
              onMouseDown={() => handleSelect(e)}
              className="px-3 py-2 cursor-pointer hover:bg-muted/60"
            >
              {e.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Add Certification Dialog ─────────────────────────────────────────────────

function AddCertDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [empId, setEmpId]           = useState("");
  const [titleMode, setTitleMode]   = useState<"list" | "custom">("list");
  const [certTypeId, setCertTypeId] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [institution, setInstitution] = useState("");
  const [dateObtained, setDateObtained] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  const { data: allEmployees = [] } = useQuery({ queryKey: ["employees"], queryFn: apiGetEmployees });
  const { data: certTypes = [] }    = useQuery({ queryKey: ["cert-types"], queryFn: apiGetCertificateTypes });

  const mutation = useMutation({
    mutationFn: () => {
      const title = titleMode === "list"
        ? certTypes.find(c => String(c.id) === certTypeId)?.name ?? ""
        : customTitle.trim();
      return apiCreateQualification(empId, {
        type: "certification",
        title,
        institution: institution.trim() || null,
        dateObtained: dateObtained || null,
        expiryDate: expiryDate || null,
      });
    },
    onSuccess: () => {
      toast.success(t("certifications.addDialog.certAdded", "Certification added"));
      queryClient.invalidateQueries({ queryKey: ["cert-overview"] });
      queryClient.invalidateQueries({ queryKey: ["cert-overview-dash"] });
      queryClient.invalidateQueries({ queryKey: ["cert-overview-analytics"] });
      onOpenChange(false);
      reset();
    },
    onError: () => toast.error(t("certifications.addDialog.errorAdding", "Failed to add certification")),
  });

  function reset() {
    setEmpId(""); setTitleMode("list"); setCertTypeId("");
    setCustomTitle(""); setInstitution(""); setDateObtained(""); setExpiryDate("");
  }

  const titleValue = titleMode === "list"
    ? certTypes.find(c => String(c.id) === certTypeId)?.name ?? ""
    : customTitle.trim();
  const canSubmit = !!empId && !!titleValue && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-blue-600" />
            {t("certifications.addDialog.certTitle", "Add Certification")}
          </DialogTitle>
          <DialogDescription>
            {t("certifications.addDialog.certDesc", "Record a certification for an employee.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          {/* Employee */}
          <div>
            <FieldLabel required>{t("certifications.addDialog.employee", "Employee")}</FieldLabel>
            <EmployeePicker
              allEmployees={allEmployees}
              empId={empId}
              onSelect={(id) => setEmpId(id)}
              placeholder={t("certifications.addDialog.searchEmployee", "Search employee...")}
            />
          </div>

          {/* Title */}
          <div>
            <FieldLabel required>{t("certifications.addDialog.certification", "Certification")}</FieldLabel>
            <div className="flex gap-2 mb-1.5">
              <button
                type="button"
                onClick={() => setTitleMode("list")}
                className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${titleMode === "list" ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 border-border text-muted-foreground hover:text-foreground"}`}
              >
                {t("certifications.addDialog.fromList", "From list")}
              </button>
              <button
                type="button"
                onClick={() => setTitleMode("custom")}
                className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${titleMode === "custom" ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 border-border text-muted-foreground hover:text-foreground"}`}
              >
                {t("certifications.addDialog.custom", "Custom")}
              </button>
            </div>
            {titleMode === "list" ? (
              <select
                value={certTypeId}
                onChange={e => setCertTypeId(e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
              >
                <option value="">{t("certifications.addDialog.selectCert", "— select certification —")}</option>
                {certTypes.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </select>
            ) : (
              <input
                type="text"
                placeholder={t("certifications.addDialog.certNamePlaceholder", "e.g. AWS Solutions Architect")}
                value={customTitle}
                onChange={e => setCustomTitle(e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
              />
            )}
          </div>

          {/* Institution */}
          <div>
            <FieldLabel>{t("certifications.addDialog.institution", "Issuing body / Institution")}</FieldLabel>
            <input
              type="text"
              placeholder={t("certifications.addDialog.institutionPlaceholder", "e.g. Amazon Web Services")}
              value={institution}
              onChange={e => setInstitution(e.target.value)}
              className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>{t("certifications.addDialog.dateObtained", "Date obtained")}</FieldLabel>
              <input type="date" value={dateObtained} onChange={e => setDateObtained(e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm" />
            </div>
            <div>
              <FieldLabel>{t("certifications.addDialog.expiryDate", "Expiry date")}</FieldLabel>
              <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => { onOpenChange(false); reset(); }}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button size="sm" onClick={() => mutation.mutate()} disabled={!canSubmit}>
              {mutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t("certifications.addDialog.save", "Add Certification")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add Training Dialog ──────────────────────────────────────────────────────

function AddTrainingDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [empId, setEmpId]             = useState("");
  const [titleMode, setTitleMode]     = useState<"list" | "custom">("list");
  const [trainingTypeId, setTrainingTypeId] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [dateObtained, setDateObtained] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceMonths, setRecurrenceMonths] = useState("");
  const [expiryDate, setExpiryDate]   = useState("");

  const { data: allEmployees = [] } = useQuery({ queryKey: ["employees"], queryFn: apiGetEmployees });
  const { data: trainingTypes = [] } = useQuery({ queryKey: ["training-types"], queryFn: apiGetTrainingTypes });

  // Auto-populate recurring fields when a training type is selected
  useEffect(() => {
    if (titleMode === "list" && trainingTypeId) {
      const type = trainingTypes.find(t => String(t.id) === trainingTypeId);
      if (type) {
        setIsRecurring(type.isRecurring);
        setRecurrenceMonths(type.recurrenceMonths ? String(type.recurrenceMonths) : "");
      }
    }
  }, [trainingTypeId, titleMode, trainingTypes]);

  // Auto-calculate expiry when dateObtained + recurrenceMonths changes
  useEffect(() => {
    if (isRecurring && dateObtained && recurrenceMonths) {
      const months = parseInt(recurrenceMonths);
      if (!isNaN(months) && months > 0) {
        const d = new Date(dateObtained);
        d.setMonth(d.getMonth() + months);
        setExpiryDate(d.toISOString().split("T")[0]);
      }
    }
  }, [isRecurring, dateObtained, recurrenceMonths]);

  const mutation = useMutation({
    mutationFn: () => {
      const title = titleMode === "list"
        ? trainingTypes.find(t => String(t.id) === trainingTypeId)?.name ?? ""
        : customTitle.trim();
      return apiCreateQualification(empId, {
        type: "training",
        title,
        institution: null,
        dateObtained: dateObtained || null,
        expiryDate: expiryDate || null,
        recurrenceMonths: isRecurring && recurrenceMonths ? parseInt(recurrenceMonths) : null,
      });
    },
    onSuccess: () => {
      toast.success(t("certifications.addDialog.trainingAdded", "Training added"));
      queryClient.invalidateQueries({ queryKey: ["training-overview"] });
      queryClient.invalidateQueries({ queryKey: ["training-overview-dash"] });
      queryClient.invalidateQueries({ queryKey: ["training-overview-analytics"] });
      onOpenChange(false);
      reset();
    },
    onError: () => toast.error(t("certifications.addDialog.errorAdding", "Failed to add training")),
  });

  function reset() {
    setEmpId(""); setTitleMode("list"); setTrainingTypeId("");
    setCustomTitle(""); setDateObtained(""); setIsRecurring(false);
    setRecurrenceMonths(""); setExpiryDate("");
  }

  const titleValue = titleMode === "list"
    ? trainingTypes.find(t => String(t.id) === trainingTypeId)?.name ?? ""
    : customTitle.trim();
  const canSubmit = !!empId && !!titleValue && !!dateObtained && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-blue-600" />
            {t("certifications.addDialog.trainingTitle", "Add Training")}
          </DialogTitle>
          <DialogDescription>
            {t("certifications.addDialog.trainingDesc", "Record a completed training for an employee.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          {/* Employee */}
          <div>
            <FieldLabel required>{t("certifications.addDialog.employee", "Employee")}</FieldLabel>
            <EmployeePicker
              allEmployees={allEmployees}
              empId={empId}
              onSelect={(id) => setEmpId(id)}
              placeholder={t("certifications.addDialog.searchEmployee", "Search employee...")}
            />
          </div>

          {/* Training */}
          <div>
            <FieldLabel required>{t("certifications.addDialog.training", "Training")}</FieldLabel>
            <div className="flex gap-2 mb-1.5">
              <button
                type="button"
                onClick={() => setTitleMode("list")}
                className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${titleMode === "list" ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 border-border text-muted-foreground hover:text-foreground"}`}
              >
                {t("certifications.addDialog.fromList", "From list")}
              </button>
              <button
                type="button"
                onClick={() => { setTitleMode("custom"); setIsRecurring(false); setRecurrenceMonths(""); }}
                className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${titleMode === "custom" ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 border-border text-muted-foreground hover:text-foreground"}`}
              >
                {t("certifications.addDialog.custom", "Custom")}
              </button>
            </div>
            {titleMode === "list" ? (
              <select
                value={trainingTypeId}
                onChange={e => setTrainingTypeId(e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
              >
                <option value="">{t("certifications.addDialog.selectTraining", "— select training —")}</option>
                {trainingTypes.map(t => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}{t.isRecurring ? ` (every ${t.recurrenceMonths}mo)` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                placeholder={t("certifications.addDialog.trainingNamePlaceholder", "e.g. Fire Safety Training")}
                value={customTitle}
                onChange={e => setCustomTitle(e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
              />
            )}
          </div>

          {/* Completion date */}
          <div>
            <FieldLabel required>{t("certifications.addDialog.completionDate", "Completion date")}</FieldLabel>
            <input type="date" value={dateObtained} onChange={e => setDateObtained(e.target.value)}
              className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm" />
          </div>

          {/* Recurring */}
          <div className="flex items-center gap-3 py-0.5">
            <input
              type="checkbox"
              id="recurring-check"
              checked={isRecurring}
              onChange={e => { setIsRecurring(e.target.checked); if (!e.target.checked) { setRecurrenceMonths(""); setExpiryDate(""); } }}
              className="h-4 w-4 rounded border-input"
            />
            <label htmlFor="recurring-check" className="text-sm text-foreground cursor-pointer">
              {t("certifications.addDialog.recurring", "Recurring training")}
            </label>
          </div>

          {isRecurring && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>{t("certifications.addDialog.recurrenceMonths", "Repeat every (months)")}</FieldLabel>
                <input
                  type="number" min={1} max={120}
                  value={recurrenceMonths}
                  onChange={e => setRecurrenceMonths(e.target.value)}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                />
              </div>
              <div>
                <FieldLabel>{t("certifications.addDialog.expiryDate", "Expiry date")}</FieldLabel>
                <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm" />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => { onOpenChange(false); reset(); }}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button size="sm" onClick={() => mutation.mutate()} disabled={!canSubmit}>
              {mutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t("certifications.addDialog.saveTraining", "Add Training")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Stat card ───────────────────────────────────────────────────────────────

function StatCard({
  label, value, icon: Icon, bg, color, active, onClick, suffix,
}: {
  label: string; value: number; icon: any;
  bg: string; color: string; active?: boolean; onClick?: () => void; suffix?: string;
}) {
  const content = (
    <>
      <div className={`h-9 w-9 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <p className="text-2xl font-bold tabular-nums text-foreground leading-none">{value}{suffix}</p>
      </div>
    </>
  );

  if (!onClick) {
    return <div className="flex items-center gap-3.5 px-5 py-4 text-left w-full">{content}</div>;
  }

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3.5 px-5 py-4 text-left w-full transition-colors
        ${active ? "bg-muted/60 ring-2 ring-inset ring-primary/30" : "hover:bg-muted/40"}`}
    >
      {content}
    </button>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function CertificationsModule() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"overview" | "certifications" | "training">("overview");

  const isHrOrAdmin = user?.role.mainRole === "hr" || user?.role.mainRole === "admin";
  const isLineManager = user?.role.subRole === "line_manager";

  if (!user || (!isHrOrAdmin && !isLineManager)) {
    return <Navigate to="/" replace />;
  }

  return (
    <AppLayout>
      <div className="w-full space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t("certifications.title", "Certifications & Training")}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{t("certifications.subtitle", "Track employee certifications and training compliance")}</p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 border-b border-border/60">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "overview"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t("certifications.tabs.overview", "Compliance Overview")}
          </button>
          <button
            onClick={() => setActiveTab("certifications")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "certifications"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t("certifications.tabs.certifications", "Certifications")}
          </button>
          <button
            onClick={() => setActiveTab("training")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "training"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t("certifications.tabs.training", "Training")}
          </button>
        </div>

        {activeTab === "overview" ? (
          <ComplianceOverviewTab t={t} />
        ) : activeTab === "certifications" ? (
          <CertOverview t={t} canWrite={isHrOrAdmin} />
        ) : (
          <TrainingOverview t={t} canWrite={isHrOrAdmin} />
        )}
      </div>
    </AppLayout>
  );
}

// ─── Compliance overview (graphs & statistics) ───────────────────────────────

function ComplianceOverviewTab({ t }: { t: any }) {
  const { data: certEmployees = [], isLoading: certLoading } = useQuery<CertComplianceEmployee[]>({
    queryKey: ["cert-overview"],
    queryFn: apiGetCertificationOverview,
  });

  const { data: trainingEmployees = [], isLoading: trainingLoading } = useQuery<TrainingComplianceEmployee[]>({
    queryKey: ["training-overview"],
    queryFn: apiGetTrainingOverview,
  });

  const isLoading = certLoading || trainingLoading;

  const certPie = useMemo(() => {
    const compliant = certEmployees.filter((e) => getCertStatus(e) === "compliant").length;
    const expiring  = certEmployees.filter((e) => getCertStatus(e) === "expiring").length;
    const action    = certEmployees.filter((e) => getCertStatus(e) === "non_compliant").length;
    return [
      { key: "compliant" as ComplianceStatus, value: compliant },
      { key: "expiring" as ComplianceStatus, value: expiring },
      { key: "non_compliant" as ComplianceStatus, value: action },
      ...(["noData", "notApplicable"] as const).map(key => ({ key, value: certEmployees.filter(employee => getCertStatus(employee) === key).length })),
    ].filter((d) => d.value > 0);
  }, [certEmployees]);

  const trainingPie = useMemo(() => {
    const compliant = trainingEmployees.filter((e) => getTrainStatus(e) === "compliant").length;
    const expiring  = trainingEmployees.filter((e) => getTrainStatus(e) === "expiring").length;
    const action    = trainingEmployees.filter((e) => getTrainStatus(e) === "non_compliant").length;
    return [
      { key: "compliant" as ComplianceStatus, value: compliant },
      { key: "expiring" as ComplianceStatus, value: expiring },
      { key: "non_compliant" as ComplianceStatus, value: action },
      ...(["noData", "notApplicable"] as const).map(key => ({ key, value: trainingEmployees.filter(employee => getTrainStatus(employee) === key).length })),
    ].filter((d) => d.value > 0);
  }, [trainingEmployees]);

  const deptStats = useMemo(() => {
    const map = new Map<string, { dept: string; certIssues: number; trainingIssues: number }>();
    certEmployees.forEach((e) => {
      const d = e.department || "—";
      if (!map.has(d)) map.set(d, { dept: d, certIssues: 0, trainingIssues: 0 });
      if (getCertStatus(e) === "non_compliant") map.get(d)!.certIssues += 1;
    });
    trainingEmployees.forEach((e) => {
      const d = e.department || "—";
      if (!map.has(d)) map.set(d, { dept: d, certIssues: 0, trainingIssues: 0 });
      if (getTrainStatus(e) === "non_compliant") map.get(d)!.trainingIssues += 1;
    });
    return Array.from(map.values())
      .filter((d) => d.certIssues > 0 || d.trainingIssues > 0)
      .sort((a, b) => (b.certIssues + b.trainingIssues) - (a.certIssues + a.trainingIssues))
      .slice(0, 8);
  }, [certEmployees, trainingEmployees]);

  const upcoming = useMemo(() => {
    const items: { employeeName: string; title: string; expiryDate: string; type: "cert" | "training" }[] = [];
    certEmployees.forEach((e) => e.expiringCerts.forEach((c) => items.push({ employeeName: e.employeeName, title: c.title, expiryDate: c.expiryDate, type: "cert" })));
    trainingEmployees.forEach((e) => e.expiringTrainings.forEach((c) => items.push({ employeeName: e.employeeName, title: c.title, expiryDate: c.expiryDate, type: "training" })));
    return items.sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime());
  }, [certEmployees, trainingEmployees]);

  const totalEmployees = Math.max(certEmployees.length, trainingEmployees.length);
  const certComplianceRate = certEmployees.length > 0
    ? Math.round((certEmployees.filter((e) => getCertStatus(e) === "compliant").length / certEmployees.length) * 100)
    : 0;
  const trainingComplianceRate = trainingEmployees.length > 0
    ? Math.round((trainingEmployees.filter((e) => getTrainStatus(e) === "compliant").length / trainingEmployees.length) * 100)
    : 0;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <StatCardsSkeleton count={4} />
        <CardGridSkeleton count={2} className="grid grid-cols-1 lg:grid-cols-2 gap-4" />
      </div>
    );
  }

  return (
    <>
      {/* KPI cards */}
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0">
          <StatCard label={t("certifications.overview.totalEmployees", "Total Employees")} value={totalEmployees}
            icon={Users} bg="bg-blue-500/10" color="text-blue-600" />
          <StatCard label={t("certifications.overview.certCompliance", "Certification Compliance")} value={certComplianceRate}
            icon={ShieldCheck} bg="bg-emerald-500/10" color="text-emerald-600" suffix="%" />
          <StatCard label={t("certifications.overview.trainingCompliance", "Training Compliance")} value={trainingComplianceRate}
            icon={BookOpen} bg="bg-indigo-500/10" color="text-indigo-600" suffix="%" />
          <StatCard label={t("certifications.overview.expiringSoon", "Expiring Soon")} value={upcoming.length}
            icon={Clock} bg="bg-amber-500/10" color="text-amber-600" />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("certifications.overview.certCompliance", "Certification Compliance")}</CardTitle>
          </CardHeader>
          <CardContent>
            {certPie.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">{t("certifications.overview.noData", "No employees found")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={certPie}
                    dataKey="value"
                    nameKey="key"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ key, value }) => `${t(`certifications.status.${key === "non_compliant" ? "nonCompliant" : key}`, key)}: ${value}`}
                  >
                    {certPie.map((d) => (
                      <Cell key={d.key} fill={COMPLIANCE_COLORS[d.key]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("certifications.overview.trainingCompliance", "Training Compliance")}</CardTitle>
          </CardHeader>
          <CardContent>
            {trainingPie.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">{t("certifications.overview.noData", "No employees found")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={trainingPie}
                    dataKey="value"
                    nameKey="key"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ key, value }) => `${t(`certifications.status.${key === "non_compliant" ? "nonCompliant" : key}`, key)}: ${value}`}
                  >
                    {trainingPie.map((d) => (
                      <Cell key={d.key} fill={COMPLIANCE_COLORS[d.key]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Issues by department */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("certifications.overview.issuesByDepartment", "Issues by Department")}</CardTitle>
          </CardHeader>
          <CardContent>
            {deptStats.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">{t("certifications.overview.noIssues", "No outstanding issues")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={deptStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="dept" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="certIssues" name={t("certifications.tabs.certifications", "Certifications")} fill={DEPT_CHART_COLORS[3]} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="trainingIssues" name={t("certifications.tabs.training", "Training")} fill={DEPT_CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Upcoming expirations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("certifications.overview.upcomingExpirations", "Upcoming Expirations")}</CardTitle>
          </CardHeader>
          <CardContent>
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">{t("certifications.overview.noIssues", "No outstanding issues")}</p>
            ) : (
              <div className="rounded-xl border border-border/60 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
                      <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("certifications.overview.employee", "Employee")}</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("certifications.overview.issues", "Issues")}</TableHead>
                      <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">{t("certifications.overview.expiresOn", "Expires")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {upcoming.slice(0, 8).map((item, i) => (
                      <TableRow key={i} className="border-b border-border/40 last:border-0">
                        <TableCell className="pl-4 py-3">
                          <p className="text-sm font-medium truncate">{item.employeeName}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.title}</p>
                        </TableCell>
                        <TableCell className="py-3">
                          <Badge variant="outline" className="text-[10px]">
                            {item.type === "cert" ? t("certifications.tabs.certifications", "Certifications") : t("certifications.tabs.training", "Training")}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-3 text-sm text-muted-foreground whitespace-nowrap">{item.expiryDate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// ─── Certifications overview ─────────────────────────────────────────────────

function CertOverview({ t, canWrite }: { t: any; canWrite: boolean }) {
  const [search, setSearch]             = useState("");
  const [deptFilter, setDeptFilter]     = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | ComplianceStatus>("all");
  const [page, setPage]                 = useState(1);
  const [addOpen, setAddOpen]           = useState(false);
  const PAGE_SIZE = 20;

  const { data: employees = [], isLoading } = useQuery<CertComplianceEmployee[]>({
    queryKey: ["cert-overview"],
    queryFn: apiGetCertificationOverview,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: apiGetDepartments,
    staleTime: 5 * 60_000,
  });

  const compliantCount = employees.filter((e) => getCertStatus(e) === "compliant").length;
  const expiringCount  = employees.filter((e) => getCertStatus(e) === "expiring").length;
  const actionCount    = employees.filter((e) => getCertStatus(e) === "non_compliant").length;
  const total          = employees.length;

  const filtered = employees.filter((emp) => {
    if (search.trim() && !emp.employeeName.toLowerCase().includes(search.toLowerCase())) return false;
    if (deptFilter !== "all" && emp.department !== deptFilter) return false;
    if (statusFilter !== "all" && getCertStatus(emp) !== statusFilter) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleStatClick = (key: "all" | ComplianceStatus) => {
    setStatusFilter((prev) => prev === key ? "all" : key);
    setDeptFilter("all"); setSearch(""); setPage(1);
  };

  return (
    <>
      <AddCertDialog open={addOpen} onOpenChange={setAddOpen} />

      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0">
          <StatCard label={t("certifications.status.compliant", "Compliant")} value={isLoading ? 0 : compliantCount}
            icon={CheckCircle2} bg="bg-emerald-500/10" color="text-emerald-600"
            active={statusFilter === "compliant"} onClick={() => handleStatClick("compliant")} />
          <StatCard label={t("certifications.status.expiring", "Expiring")} value={isLoading ? 0 : expiringCount}
            icon={AlertTriangle} bg="bg-amber-500/10" color="text-amber-600"
            active={statusFilter === "expiring"} onClick={() => handleStatClick("expiring")} />
          <StatCard label={t("certifications.status.actionNeeded", "Action needed")} value={isLoading ? 0 : actionCount}
            icon={XCircle} bg="bg-red-500/10" color="text-red-600"
            active={statusFilter === "non_compliant"} onClick={() => handleStatClick("non_compliant")} />
          <StatCard label={t("certifications.overview.total", "Total employees")} value={isLoading ? 0 : total}
            icon={Users} bg="bg-blue-500/10" color="text-blue-600"
            active={statusFilter === "all" && deptFilter === "all" && !search} onClick={() => handleStatClick("all")} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={t("certifications.overview.searchPlaceholder", "Search employees...")}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm" />
        </div>
        <select value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm">
          <option value="all">{t("organization.allDepartments", "All Departments")}</option>
          {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1); }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm">
          <option value="all">{t("certifications.status.all", "All Statuses")}</option>
          <option value="compliant">{t("certifications.status.compliant", "Compliant")}</option>
          <option value="expiring">{t("certifications.status.expiring", "Expiring")}</option>
          <option value="noData">{t("certifications.status.noData")}</option>
          <option value="notApplicable">{t("certifications.status.notApplicable")}</option>
          <option value="non_compliant">{t("certifications.status.actionNeeded", "Action needed")}</option>
        </select>
        {!isLoading && (
          <span className="text-xs text-muted-foreground">
            {filtered.length} {t("certifications.overview.employees", "employees")}
          </span>
        )}
        {canWrite && (
          <Button size="sm" onClick={() => setAddOpen(true)} className="ml-auto gradient-brand text-primary-foreground">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            {t("certifications.addDialog.addCertBtn", "Add Certification")}
          </Button>
        )}
      </div>

      <OverviewTable rows={paginated} isLoading={isLoading}
        getStatus={getCertStatus}
        getRequired={(e) => e.requiredCerts.length}
        getIssues={(e) => [
          ...e.missingCerts.map((c) => `Missing: ${c}`),
          ...e.expiredCerts.map((c) => `Expired: ${c.title}`),
          ...e.expiringCerts.map((c) => `Expiring: ${c.title} (${c.expiryDate})`),
        ]}
        t={t}
      />

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Training overview ───────────────────────────────────────────────────────

function TrainingOverview({ t, canWrite }: { t: any; canWrite: boolean }) {
  const [search, setSearch]             = useState("");
  const [deptFilter, setDeptFilter]     = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | ComplianceStatus>("all");
  const [page, setPage]                 = useState(1);
  const [addOpen, setAddOpen]           = useState(false);
  const PAGE_SIZE = 20;

  const { data: employees = [], isLoading } = useQuery<TrainingComplianceEmployee[]>({
    queryKey: ["training-overview"],
    queryFn: apiGetTrainingOverview,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: apiGetDepartments,
    staleTime: 5 * 60_000,
  });

  const compliantCount = employees.filter((e) => getTrainStatus(e) === "compliant").length;
  const expiringCount  = employees.filter((e) => getTrainStatus(e) === "expiring").length;
  const actionCount    = employees.filter((e) => getTrainStatus(e) === "non_compliant").length;
  const total          = employees.length;

  const filtered = employees.filter((emp) => {
    if (search.trim() && !emp.employeeName.toLowerCase().includes(search.toLowerCase())) return false;
    if (deptFilter !== "all" && emp.department !== deptFilter) return false;
    if (statusFilter !== "all" && getTrainStatus(emp) !== statusFilter) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleStatClick = (key: "all" | ComplianceStatus) => {
    setStatusFilter((prev) => prev === key ? "all" : key);
    setDeptFilter("all"); setSearch(""); setPage(1);
  };

  return (
    <>
      <AddTrainingDialog open={addOpen} onOpenChange={setAddOpen} />

      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0">
          <StatCard label={t("certifications.status.compliant", "Compliant")} value={isLoading ? 0 : compliantCount}
            icon={CheckCircle2} bg="bg-emerald-500/10" color="text-emerald-600"
            active={statusFilter === "compliant"} onClick={() => handleStatClick("compliant")} />
          <StatCard label={t("certifications.status.expiring", "Expiring")} value={isLoading ? 0 : expiringCount}
            icon={AlertTriangle} bg="bg-amber-500/10" color="text-amber-600"
            active={statusFilter === "expiring"} onClick={() => handleStatClick("expiring")} />
          <StatCard label={t("certifications.status.actionNeeded", "Action needed")} value={isLoading ? 0 : actionCount}
            icon={XCircle} bg="bg-red-500/10" color="text-red-600"
            active={statusFilter === "non_compliant"} onClick={() => handleStatClick("non_compliant")} />
          <StatCard label={t("certifications.overview.total", "Total employees")} value={isLoading ? 0 : total}
            icon={Users} bg="bg-blue-500/10" color="text-blue-600"
            active={statusFilter === "all" && deptFilter === "all" && !search} onClick={() => handleStatClick("all")} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={t("certifications.overview.searchPlaceholder", "Search employees...")}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm" />
        </div>
        <select value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm">
          <option value="all">{t("organization.allDepartments", "All Departments")}</option>
          {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1); }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm">
          <option value="all">{t("certifications.status.all", "All Statuses")}</option>
          <option value="compliant">{t("certifications.status.compliant", "Compliant")}</option>
          <option value="expiring">{t("certifications.status.expiring", "Expiring")}</option>
          <option value="noData">{t("certifications.status.noData")}</option>
          <option value="notApplicable">{t("certifications.status.notApplicable")}</option>
          <option value="non_compliant">{t("certifications.status.actionNeeded", "Action needed")}</option>
        </select>
        {!isLoading && (
          <span className="text-xs text-muted-foreground">
            {filtered.length} {t("certifications.overview.employees", "employees")}
          </span>
        )}
        {canWrite && (
          <Button size="sm" onClick={() => setAddOpen(true)} className="ml-auto gradient-brand text-primary-foreground">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            {t("certifications.addDialog.addTrainingBtn", "Add Training")}
          </Button>
        )}
      </div>

      <OverviewTable rows={paginated} isLoading={isLoading}
        getStatus={getTrainStatus}
        getRequired={(e) => e.requiredTrainings.length}
        getIssues={(e) => [
          ...e.missingTrainings.map((c) => `Missing: ${c}`),
          ...e.expiredTrainings.map((c) => `Expired: ${c.title}`),
          ...e.expiringTrainings.map((c) => `Expiring: ${c.title} (${c.expiryDate})`),
        ]}
        emptyIcon={BookOpen}
        t={t}
      />

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Shared overview table ───────────────────────────────────────────────────

type AnyComplianceEmployee = {
  employeeId: string;
  employeeName: string;
  avatarUrl: string | null;
  avatarColor: string | null;
  initials: string;
  department: string;
  positionTitle: string | null;
};

function OverviewTable<T extends AnyComplianceEmployee>({
  rows, isLoading, getStatus, getRequired, getIssues, emptyIcon: EmptyIcon = ShieldCheck, t,
}: {
  rows: T[];
  isLoading: boolean;
  getStatus: (e: T) => ComplianceStatus;
  getRequired: (e: T) => number;
  getIssues: (e: T) => string[];
  emptyIcon?: any;
  t: any;
}) {
  if (isLoading) {
    return (
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden p-4">
        <TableSkeleton rows={6} cols={6} />
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border/60 bg-muted/30 hover:bg-muted/30">
            <TableHead className="pl-4 font-semibold text-xs text-muted-foreground uppercase tracking-wide">
              {t("certifications.overview.employee", "Employee")}
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden md:table-cell">
              {t("certifications.position", "Position")}
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden lg:table-cell">
              {t("certifications.overview.required", "Required")}
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide hidden xl:table-cell">
              {t("certifications.overview.issues", "Issues")}
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
              {t("certifications.overview.status", "Status")}
            </TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="py-12">
                <div className="flex flex-col items-center gap-3 text-center">
                  <EmptyIcon className="h-8 w-8 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">{t("certifications.overview.noData", "No employees found")}</p>
                </div>
              </TableCell>
            </TableRow>
          ) : rows.map((emp) => {
            const st = getStatus(emp);
            const sc = STATUS_CFG[st];
            const StatusIcon = sc.icon;
            const issues = getIssues(emp);
            return (
              <TableRow key={emp.employeeId}
                className="border-b border-border/40 cursor-pointer hover:bg-muted/40 transition-colors">
                <TableCell className="pl-4 py-3">
                  <div className="flex items-center gap-3">
                    <EmployeeAvatar avatarUrl={emp.avatarUrl} avatarColor={emp.avatarColor}
                      initials={emp.initials} size="sm" className="shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{emp.employeeName}</p>
                      <p className="text-xs text-muted-foreground truncate">{emp.department || "—"}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-3 hidden md:table-cell">
                  <span className="text-sm text-muted-foreground">{emp.positionTitle ?? "—"}</span>
                </TableCell>
                <TableCell className="py-3 hidden lg:table-cell text-center">
                  <span className="text-sm">{getRequired(emp)}</span>
                </TableCell>
                <TableCell className="py-3 hidden xl:table-cell">
                  {issues.length > 0 ? (
                    <div className="flex flex-col gap-0.5">
                      {issues.map((issue, i) => (
                        <span key={i} className="text-xs text-muted-foreground">{issue}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="py-3">
                  <Badge variant="outline" className={`text-[10px] border ${sc.badge}`}>
                    <StatusIcon className="h-3 w-3 mr-0.5" />{sc.label}
                  </Badge>
                </TableCell>
                <TableCell className="py-3 w-10">
                  <Link to={`/employees/${emp.employeeId}`}>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
